/**
 * Словари переводов держатся в браузере между загрузками.
 *
 * Смена окружения — это перезагрузка страницы, поэтому без кеша всё начинается
 * с нуля: навбар и заголовки таблиц рисуются ключами `DTK_*`, пока не приедет
 * словарь. Сам запрос быстрый (132 КБ, ~70 мс), некрасиво выходит из-за
 * порядка: сначала первый кадр, потом словарь. Кеш даёт готовый словарь ещё до
 * первого кадра, а сеть остаётся ревалидацией.
 *
 * Ключ включает дистрибьютора и клиента, потому что и пространство имён
 * бекенда такое же (`{module}${distrId}${clientId}`): словарь переопределяется
 * на дистрибьютора, и подставить чужой — значит показать чужую терминологию.
 */

const PREFIX = 'ism.i18n.v1.';
/** Сколько словарей помним. `common` — 132 КБ, модули — единицы килобайт. */
const MAX_ENTRIES = 12;

export interface BundleIdentity {
  lng: string;
  ns: string;
  distrId: number;
  clientId: number;
}

type Bundle = Record<string, string>;

function key({ lng, ns, distrId, clientId }: BundleIdentity): string {
  return `${PREFIX}${lng}|${ns}|${distrId}|${clientId}`;
}

export function readBundle(id: BundleIdentity): Bundle | null {
  try {
    const raw = localStorage.getItem(key(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; data: Bundle };
    return parsed?.data ?? null;
  } catch {
    // Приватный режим, выключенное хранилище или битая запись — не повод падать.
    return null;
  }
}

export function writeBundle(id: BundleIdentity, data: Bundle): void {
  // Пустой ответ бекенда (`{}` для неизвестного языка или до сессии) кешировать
  // нельзя: он перекроет живой словарь на следующей загрузке.
  if (!data || Object.keys(data).length === 0) return;
  const payload = JSON.stringify({ at: Date.now(), data });
  try {
    localStorage.setItem(key(id), payload);
  } catch {
    // Квота. Освобождаем место и пробуем один раз — если не вышло, живём без кеша.
    evict(MAX_ENTRIES / 2);
    try {
      localStorage.setItem(key(id), payload);
    } catch { /* без кеша тоже работает */ }
    return;
  }
  evict(MAX_ENTRIES);
}

/** Оставляет самые свежие `keep` словарей. */
function evict(keep: number): void {
  try {
    const entries: Array<{ k: string; at: number }> = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PREFIX)) continue;
      let at = 0;
      try {
        at = (JSON.parse(localStorage.getItem(k) || '{}') as { at?: number }).at ?? 0;
      } catch { /* битую запись считаем самой старой */ }
      entries.push({ k, at });
    }
    if (entries.length <= keep) return;
    entries.sort((a, b) => b.at - a.at);
    for (const { k } of entries.slice(keep)) localStorage.removeItem(k);
  } catch { /* best-effort */ }
}
